import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useState } from "react";
import { navigateWithPageReload } from "utils/navigation";
import { ROUTE_PATHS } from "utils/routerDom";

import type { ContextValue } from "../model/types";

import { useEnforceAddressStep } from "../hooks/useEnforceAddressStep";
import { useHandleRecenter } from "../hooks/useHandleRecenter";
import { usePrefillFormData } from "../hooks/usePrefillFormData";
import { useStepNoUrlSync } from "../hooks/useStepNoUrlSync";
import {
  AddressInfo,
  Coordinates,
  ModernizationElement,
  OutlinePolygon,
  SingleBuildingFormState,
} from "../model/types";

const DEFAULT_CENTER: Coordinates = {
  lat: 52.52,
  lng: 13.405,
};

const DEFAULT_MODERNIZATION_ELEMENT: ModernizationElement = {
  modernization_date: undefined,
  modernized: false,
};

const defaultValue: ContextValue = {
  callForRecenter: () => {},
  canExecuteDTCreation: true,
  defaultCenter: DEFAULT_CENTER,
  dismissEpcPrompt: () => {},
  formData: {
    buildingCenter: DEFAULT_CENTER,
    epcRawExtractionData: undefined,
    extraDetails: {
      has_cooling: false,
      has_pv: false,
      heating_system: undefined,
      installation_date: undefined,
      mechanical_ventilation: false,
      modernizationElements: {
        facade: DEFAULT_MODERNIZATION_ELEMENT,
        rooftop: DEFAULT_MODERNIZATION_ELEMENT,
        windows: DEFAULT_MODERNIZATION_ELEMENT,
      },
      modernized: false,
    },
    images: [],
    outline: [],
    prefilledOutline: false,
  },
  hasDismissedEpcPrompt: false,
  isPrefillingFormData: false,
  jobId: "",
  onStartNewBuilding: () => {},
  outlineState: {
    isErrorOutline: false,
    isLoadingOutline: false,
  },
  pauseInteraction: false,
  projectId: undefined,
  recenterRequestFlag: 0,
  setAddress: () => {},
  setBuildingOutline: () => {},
  setCanExecuteDTCreation: () => {},
  setFormData: () => {},
  setJobId: () => {},
  setMapCenter: () => {},
  setOutlineState: () => {},
  setPauseInteractions: () => {},
  setStepNo: () => {},
  stepNo: 0,
};

const outlineStateDefaultValue = {
  isErrorOutline: false,
  isLoadingOutline: false,
};

export const SingleBuildingContext = createContext<ContextValue>(defaultValue);

const useDefineHandlers = (setFormData: Dispatch<SetStateAction<SingleBuildingFormState>>) => {
  const setAddress = useCallback(
    (addressInfo: AddressInfo) => {
      setFormData((prev) => ({
        ...prev,
        addressInfo: { ...prev.addressInfo, ...addressInfo },
      }));
    },
    [setFormData]
  );

  const setMapCenter = useCallback(
    (center: Coordinates) => {
      setFormData((prev) => ({
        ...prev,
        buildingCenter: center,
      }));
    },
    [setFormData]
  );

  const setBuildingOutline = useCallback(
    (outline: OutlinePolygon) => {
      setFormData((prev) => ({
        ...prev,
        outline: outline,
      }));
    },
    [setFormData]
  );

  const onStartNewBuilding = () => {
    navigateWithPageReload({ replace: true, url: ROUTE_PATHS.addBuilding().navigateUrl });
  };

  return {
    onStartNewBuilding,
    setAddress,
    setBuildingOutline,
    setMapCenter,
  };
};

const useDefineStates = () => {
  const { setStepNo, stepNo } = useStepNoUrlSync();
  const [formData, setFormData] = useState<ContextValue["formData"]>(defaultValue.formData);
  const [outlineState, setOutlineState] = useState(outlineStateDefaultValue);
  const [jobId, setJobId] = useState<string>("");
  const [canExecuteDTCreation, setCanExecuteDTCreation] = useState<boolean>(true);
  const [hasDismissedEpcPrompt, setHasDismissedEpcPrompt] = useState(false);
  const dismissEpcPrompt = useCallback(() => setHasDismissedEpcPrompt(true), []);

  return {
    canExecuteDTCreation,
    dismissEpcPrompt,
    formData,
    hasDismissedEpcPrompt,
    jobId,
    outlineState,
    setCanExecuteDTCreation,
    setFormData,
    setJobId,
    setOutlineState,
    setStepNo,
    stepNo,
  };
};

export const SingleBuildingContextProvider = ({
  children,
  projectId,
}: {
  children: ReactNode;
  projectId: string | undefined;
}) => {
  const {
    canExecuteDTCreation,
    dismissEpcPrompt,
    formData,
    hasDismissedEpcPrompt,
    jobId,
    outlineState,
    setCanExecuteDTCreation,
    setFormData,
    setJobId,
    setOutlineState,
    setStepNo,
    stepNo,
  } = useDefineStates();

  const { callForRecenter, recenterRequestFlag } = useHandleRecenter();

  const { isPrefillingFormData, pauseInteraction, setPauseInteractions } = usePrefillFormData({
    projectId,
    setFormData,
  });

  const { onStartNewBuilding, setAddress, setBuildingOutline, setMapCenter } = useDefineHandlers(setFormData);

  useEnforceAddressStep({
    formData,
    isPrefillingFormData,
    projectId,
    setStepNo,
    stepNo,
  });

  return (
    <SingleBuildingContext.Provider
      value={{
        callForRecenter,
        canExecuteDTCreation,
        defaultCenter: DEFAULT_CENTER,
        dismissEpcPrompt,
        formData,
        hasDismissedEpcPrompt,
        isPrefillingFormData,
        jobId,
        onStartNewBuilding,
        outlineState,
        pauseInteraction,
        projectId,
        recenterRequestFlag,
        setAddress,
        setBuildingOutline,
        setCanExecuteDTCreation,
        setFormData,
        setJobId,
        setMapCenter,
        setOutlineState,
        setPauseInteractions,
        setStepNo,
        stepNo,
      }}
    >
      {children}
    </SingleBuildingContext.Provider>
  );
};
